// [S-802] Show a template's variables while it is being edited.
//
// A variable is not IN the document: a tag set records it as a pair of offsets
// into `templateTextForTagging(html)`, kept beside the template. So an author
// editing a certified template sees a row of underscores with nothing to say
// which of them a deal will fill, which is the one thing they most need to
// know before changing the text around it.
//
// The editor is given a MARKED-UP COPY — each slot wrapped in `<variable>` —
// and what it gives back is unmarked before it is saved. The stored template
// never contains the markup, so nothing downstream (the review engine, the
// generator, an export, an attestation hash) sees anything new. If the marking
// cannot be placed with confidence it is simply not drawn; a highlight is worth
// less than a document that still says what it said.
import { flattenedToHtmlOffsets, htmlSpanOf, templateTextForTagging } from "@csai/core/browser";

/** A slot, as the variables API returns it. */
export type HighlightSlot = { id: string; start: number; end: number; sameAs?: string | null };

// A <variable> element does not survive CKEditor's EDITING view: it renders as
// `<span data-ck-unsafe-element="variable">` with its text dropped, so the
// author watches the gap itself disappear. (The DATA round-trip is fine — that
// is what the S-805 spike measured — but the view is a different thing, and
// only the view is what an author sees.) A span with a class is native, keeps
// its text, and General HTML Support preserves the class and data attribute.
const MARK_TAG = "span";
const OPEN = /<span\b[^>]*\bclass="tv-mark"[^>]*>/gi;

/** The template with each slot wrapped, for display only. Slots whose span the
 *  map cannot vouch for, or that hold markup of their own, are left alone. */
export function withVariableMarks(html: string, slots: readonly HighlightSlot[]): string {
  if (slots.length === 0) return html;
  const map = flattenedToHtmlOffsets(html, templateTextForTagging(html));
  const edits: Array<{ from: number; to: number; text: string }> = [];
  for (const slot of slots) {
    const span = htmlSpanOf(map, slot.start, slot.end);
    if (span === null) continue;
    const inner = html.slice(span.from, span.to);
    // A slot split by markup would need the tag to wrap that markup too, and a
    // wrongly-nested tag is a corrupted document. Skip it.
    if (inner.includes("<") || inner.length === 0) continue;
    const name = slot.sameAs ?? slot.id;
    edits.push({
      from: span.from,
      to: span.to,
      text: `<${MARK_TAG} class="tv-mark" data-name="${escapeAttr(name)}">${inner}</${MARK_TAG}>`,
    });
  }
  let out = html;
  for (const e of [...edits].sort((a, b) => b.from - a.from)) {
    out = out.slice(0, e.from) + e.text + out.slice(e.to);
  }
  return out;
}

/** Everything the marking added, removed — what gets saved. The text between
 *  the tags is untouched, so a document that was only marked comes back as it
 *  went in, and one the author edited comes back with exactly their edit. */
export function withoutVariableMarks(html: string): string {
  // Remove each opening mark and ITS closing tag — not every </span>, which
  // would unbalance spans the template already had.
  let out = "";
  let rest = html;
  for (;;) {
    OPEN.lastIndex = 0;
    const open = OPEN.exec(rest);
    if (open === null) return out + rest;
    out += rest.slice(0, open.index);
    const after = rest.slice(open.index + open[0].length);
    const close = closingSpan(after);
    if (close < 0) return out + after;
    out += after.slice(0, close);
    rest = after.slice(close + "</span>".length);
  }
}

/** Index of the </span> that closes the mark, allowing nested spans inside. */
function closingSpan(html: string): number {
  let depth = 0;
  const re = /<span\b[^>]*>|<\/span>/gi;
  for (let m = re.exec(html); m !== null; m = re.exec(html)) {
    if (m[0].startsWith("</")) {
      if (depth === 0) return m.index;
      depth -= 1;
    } else depth += 1;
  }
  return -1;
}

/** Does this HTML carry any marking? Used to avoid re-marking a document the
 *  author is already editing, which would move their cursor. */
export function hasVariableMarks(html: string): boolean {
  return /class="tv-mark"/i.test(html);
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}
